import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';

export default function About() {
  return (
    <div className="bg-brand-bg">
      <SEO
        title="Om Pabst Teknik ApS | Erfaren Port- og Sikringspartner i Vejen"
        description="Læs om Pabst Teknik ApS i Vejen. Vi er specialister i professionel portservice, lovpligtigt eftersyn af porte, Lindab portløsninger og intelligente Ajax alarmsystemer i hele Danmark."
        url="/om-os"
        keywords="om Pabst Teknik, portservice Vejen, Christiansfeld, portmontør, Lindab salgspartner, Ajax installatør, industriporte, garageporte Vejen"
      />
      {/* Page Header */}
      <section className="relative bg-gradient-to-br from-slate-950 via-brand-dark to-brand-accent/20 text-white px-6 py-24 md:py-32 overflow-hidden">
        {/* Background Image with Blend Mode */}
        <div className="absolute inset-0 opacity-15 mix-blend-overlay">
          <img
            src="https://assets.auxodev.dk/assets/a2859b51-1b34-4d15-a97b-9fa5c0c0144f/webp-house-scaled-66-11zon.webp"
            alt="Pabst Teknik ApS – Portservice og sikring i Vejen og Syddanmark"
            className="h-full w-full object-cover"
          />
        </div>
        
        {/* Subtle Tech Grid Overlay */}
        <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(2,132,199,0.08)_1px,transparent_1px),linear-gradient(to_bottom,rgba(2,132,199,0.08)_1px,transparent_1px)] bg-[size:3rem_3rem] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_50%,#000_70%,transparent_100%)]" />
        
        {/* Glowing Ambient Light */}
        <div className="absolute -top-40 -right-40 w-96 h-96 bg-brand-accent/20 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-brand-accent/10 rounded-full blur-3xl pointer-events-none" />

        <div className="mx-auto max-w-7xl relative z-10 flex flex-col gap-6 items-center text-center">
          {/* Breadcrumb Navigation */}
          <nav aria-label="Brødkrummesti" className="flex items-center gap-2 text-xs sm:text-sm font-medium text-slate-300">
            <Link to="/" className="hover:text-white transition-colors flex items-center gap-1.5">
              <i className="fa-solid fa-house text-xs text-brand-accent" />
              <span>Forside</span>
            </Link>
            <i className="fa-solid fa-chevron-right text-[10px] text-slate-400" />
            <span className="text-brand-accent font-semibold" aria-current="page">Om os</span>
          </nav>

          <h1 className="text-4xl font-extrabold tracking-tight text-white sm:text-6xl font-display">
            Om <span className="text-transparent bg-clip-text bg-gradient-to-r from-white via-slate-100 to-brand-accent">Pabst Teknik ApS</span>
          </h1>
          
          <p className="text-lg text-slate-300 max-w-2xl mx-auto leading-relaxed font-sans">
            Din lokale port- og sikringspartner i Vejen med speciale i professionel portservice, alle typer porte og intelligente alarmsystemer.
          </p>
        </div>
      </section>

      {/* Story Section */}
      <section className="relative z-20 px-6 pb-12 md:pb-16 lg:pb-32">
        <div className="mx-auto max-w-7xl -mt-12 bg-white rounded-3xl border border-slate-100 p-8 md:p-16 shadow-xl">
          <div className="grid grid-cols-1 gap-16 lg:grid-cols-12 items-center">
            {/* Text Content */}
            <div className="lg:col-span-7 flex flex-col gap-6">
              <span className="text-xs font-bold text-brand-accent uppercase tracking-widest">Vores Historie</span>
              <h2 className="text-3xl font-extrabold text-brand-dark sm:text-4xl">
                Teknisk ekspertise og kompromisløs kvalitet
              </h2>
              <p className="text-slate-600 leading-relaxed">
                <Link to="/" className="font-semibold text-brand-dark hover:text-brand-accent transition-colors underline decoration-brand-accent/40 decoration-2 underline-offset-2">Pabst Teknik ApS</Link> blev stiftet med en klar vision: At levere professionel portservice, holdbare portløsninger og tekniske sikringssystemer af absolut højeste kvalitet til både private boligejere og erhvervsvirksomheder. Med base i Vejen dækker vi hele Sydøstjylland samt Trekantområdet (herunder Kolding, Christiansfeld, Vejle, Fredericia og Billund) med hurtig og professionel service.
              </p>
              <p className="text-slate-600 leading-relaxed">
                Portservice og montage af porte har fra start været vores primære kerneforretning – vi servicerer, reparerer og monterer alle typer porte, herunder garageporte, friporte, industriporte og hurtigporte samt udfører årlige lovpligtige eftersyn. Som en stærk, nyere gren tilbyder vi nu også intelligente, trådløse sikringsløsninger som autoriseret forhandler og installatør af Ajax Systems.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mt-4">
                <Link to="/ydelser" className="flex gap-3 items-start group">
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-brand-accent-light text-brand-accent mt-0.5 group-hover:bg-brand-accent group-hover:text-white transition-colors">
                    <i className="fa-solid fa-check text-xs" />
                  </span>
                  <p className="text-sm font-semibold text-slate-700 group-hover:text-brand-accent transition-colors">Salgspartner & service for Lindab porte</p>
                </Link>
                <Link to="/ydelser/alarmsystem" className="flex gap-3 items-start group">
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-brand-accent-light text-brand-accent mt-0.5 group-hover:bg-brand-accent group-hover:text-white transition-colors">
                    <i className="fa-solid fa-check text-xs" />
                  </span>
                  <p className="text-sm font-semibold text-slate-700 group-hover:text-brand-accent transition-colors">Autoriseret Ajax Systems Installatør</p>
                </Link>
                <Link to="/ydelser/garageporte-og-tilbehoer" className="flex gap-3 items-start group">
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-brand-accent-light text-brand-accent mt-0.5 group-hover:bg-brand-accent group-hover:text-white transition-colors">
                    <i className="fa-solid fa-check text-xs" />
                  </span>
                  <p className="text-sm font-semibold text-slate-700 group-hover:text-brand-accent transition-colors">Specialister i ismartgate adgangskontrol</p>
                </Link>
                <Link to="/ydelser/portservice-og-lovpligtigt-eftersyn" className="flex gap-3 items-start group">
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-brand-accent-light text-brand-accent mt-0.5 group-hover:bg-brand-accent group-hover:text-white transition-colors">
                    <i className="fa-solid fa-check text-xs" />
                  </span>
                  <p className="text-sm font-semibold text-slate-700 group-hover:text-brand-accent transition-colors">Årligt lovpligtigt eftersyn af porte</p>
                </Link>
              </div>

              {/* Quick internal link buttons */}
              <div className="flex flex-wrap items-center gap-3 pt-2">
                <Link
                  to="/ydelser"
                  className="inline-flex items-center gap-2 rounded-xl bg-brand-dark px-5 py-2.5 text-xs sm:text-sm font-bold text-white shadow-xs hover:bg-slate-800 transition-all"
                >
                  <span>Udforsk alle vores ydelser</span>
                  <i className="fa-regular fa-arrow-right text-xs text-brand-accent" />
                </Link>
                <Link
                  to="/galleri"
                  className="inline-flex items-center gap-2 rounded-xl bg-slate-100 px-5 py-2.5 text-xs sm:text-sm font-bold text-brand-dark hover:bg-slate-200 transition-all"
                >
                  <i className="fa-regular fa-images text-xs text-brand-accent" />
                  <span>Se billeder i galleriet</span>
                </Link>
              </div>

              {/* Partner Callout */}
              <div className="mt-2 pt-6 border-t border-slate-100 flex flex-col sm:flex-row items-start sm:items-center gap-4 bg-slate-50/80 p-4 rounded-2xl border border-slate-100">
                <p className="text-xs sm:text-sm text-slate-600">
                  <strong className="text-brand-dark font-semibold">Salgspartner med Lindab:</strong> Lindab fungerer som en stærk salgspartner for vores professionelle portservice. Sammen sikrer vi kvalitetstestede portløsninger, montage og vedligeholdelse.
                </p>
              </div>
            </div>

            {/* Asymmetric Image Collage */}
            <div className="lg:col-span-5 relative">
              <div className="relative mx-auto max-w-[400px] lg:max-w-none">
                <div className="overflow-hidden rounded-3xl border-4 border-slate-100 bg-white shadow-xl rotate-[-2deg]">
                  <img
                    src="https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=85,format=auto/assets/a2859b51-1b34-4d15-a97b-9fa5c0c0144f/webp-img20240717114443-69-11zon.webp"
                    alt="Pabst Teknik servicemontør udfører montage og kontrol af industriport"
                    className="h-[320px] w-full object-cover md:h-[380px]"
                  />
                </div>
                <div className="absolute -right-6 -bottom-6 w-1/2 overflow-hidden rounded-2xl border-4 border-white bg-white shadow-2xl rotate-[4deg] hidden sm:block">
                  <img
                    src="https://assets.auxodev.dk/cdn-cgi/image/width=300,quality=85,format=auto/assets/a2859b51-1b34-4d15-a97b-9fa5c0c0144f/webp-20210903-1417121360-30-11zon.webp"
                    alt="Teknisk justering af portens fjedre og mekaniske dele ved serviceeftersyn"
                    className="h-[160px] w-full object-cover"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="px-6 pt-10 pb-24 md:pt-14 md:pb-32 bg-slate-50">
        <div className="mx-auto max-w-7xl">
          <div className="text-center max-w-3xl mx-auto mb-16 flex flex-col gap-4">
            <span className="text-xs font-bold text-brand-accent uppercase tracking-widest">Vores Værdier</span>
            <h2 className="text-3xl font-extrabold text-brand-dark sm:text-4xl">
              Det vi står for hos Pabst Teknik ApS
            </h2>
            <p className="text-slate-500 leading-relaxed">
              Vores værdier er fundamentet for alt, hvad vi gør – fra den første rådgivning til den færdige installation og efterfølgende service.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
            {/* Value 1 */}
            <div className="rounded-2xl border border-slate-100 bg-white p-8 shadow-sm flex flex-col gap-4">
              <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-brand-accent-light text-brand-accent">
                <i className="fa-regular fa-shield-check text-xl" />
              </span>
              <h3 className="font-display text-lg font-bold text-brand-dark">Pålidelighed & Tillid</h3>
              <p className="text-sm text-slate-500 leading-relaxed">
                Når det handler om sikring og alarmsystemer, er tillid altafgørende. Vi holder vores aftaler, overholder tidsplaner og leverer altid et stykke arbejde, du kan stole på 100%.
              </p>
            </div>

            {/* Value 2 */}
            <div className="rounded-2xl border border-slate-100 bg-white p-8 shadow-sm flex flex-col gap-4">
              <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-brand-accent-light text-brand-accent">
                <i className="fa-regular fa-microchip text-xl" />
              </span>
              <h3 className="font-display text-lg font-bold text-brand-dark">Teknisk Innovation</h3>
              <p className="text-sm text-slate-500 leading-relaxed">
                Verden udvikler sig, og det gør sikringsteknikken også. Vi holder os konstant opdateret med de nyeste teknologier, så vi altid kan tilbyde dig de mest intelligente og brugervenlige løsninger.
              </p>
            </div>

            {/* Value 3 */}
            <div className="rounded-2xl border border-slate-100 bg-white p-8 shadow-sm flex flex-col gap-4 md:col-span-2 lg:col-span-1">
              <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-brand-accent-light text-brand-accent">
                <i className="fa-regular fa-house-building text-xl" />
              </span>
              <h3 className="font-display text-lg font-bold text-brand-dark">Lokal Forankring</h3>
              <p className="text-sm text-slate-500 leading-relaxed">
                Vi er stolte af at være en lokal virksomhed i Vejen. For dig betyder det kort afstand, hurtig responstid ved akutte problemer og en personlig service, hvor vi kender nærområdet – herunder Kolding, Christiansfeld, Vejle, Fredericia og resten af Trekantområdet.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 2 Companies - 1 Concept Collaboration Section */}
      <section className="px-6 py-10 sm:py-12 bg-slate-50 border-t border-slate-200/70">
        <div className="mx-auto max-w-7xl">
          <div className="rounded-2xl border border-slate-200/80 bg-white p-6 sm:p-8 shadow-xs">
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
              <div className="flex-1 flex flex-col gap-3">
                <div className="flex flex-wrap items-center gap-2 text-xs font-semibold text-slate-500">
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-slate-100 text-slate-700 font-bold">
                    <i className="fa-solid fa-handshake text-brand-accent text-xs" /> Samarbejde
                  </span>
                  <span>•</span>
                  <span>2 separate firmaer – 1 fælles koncept</span>
                </div>
                <h3 className="text-xl sm:text-2xl font-bold text-brand-dark">
                  Landsdækkende rækkevidde & tæt samarbejde
                </h3>
                <p className="text-sm sm:text-base text-slate-600 leading-relaxed max-w-3xl">
                  Vi er to selvstændige virksomheder – <strong>Pabst Teknik ApS</strong> og <strong>Dansk Portservice Claus Hygum ApS</strong> – der arbejder tæt sammen omkring ét gennemprøvet koncept. Med god intern kommunikation og klare standarder sikrer vi, at du som kunde altid får en tryg og fyldestgørende oplevelse fra start til slut på tværs af landet.
                </p>
                <div className="flex flex-wrap items-center gap-y-2 gap-x-6 pt-1 text-xs text-slate-500 font-medium">
                  <span className="flex items-center gap-1.5">
                    <i className="fa-solid fa-circle-check text-brand-accent" /> Fælles kvalitetsstandarder
                  </span>
                  <span className="flex items-center gap-1.5">
                    <i className="fa-solid fa-circle-check text-brand-accent" /> Hurtig koordinering
                  </span>
                  <span className="flex items-center gap-1.5">
                    <i className="fa-solid fa-circle-check text-brand-accent" /> Dækker hele landet
                  </span>
                </div>
              </div>

              <div className="lg:pl-6 lg:border-l lg:border-slate-100 flex flex-col sm:flex-row lg:flex-col items-start gap-3 shrink-0">
                <a
                  href="https://0298fb46-c259-4129-b655-7c68adb8d499.sites.auxodev.dk/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 rounded-xl border border-slate-300 hover:border-slate-400 bg-slate-50 hover:bg-slate-100 text-brand-dark px-4 py-2.5 text-xs sm:text-sm font-bold transition-all shadow-xs"
                >
                  <span>Besøg Dansk Portservice Claus Hygum ApS</span>
                  <i className="fa-solid fa-arrow-up-right-from-square text-xs text-brand-accent" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Internal Navigation & Related Links Hub */}
      <section className="px-6 py-16 bg-white border-t border-slate-100">
        <div className="mx-auto max-w-7xl">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
            <div>
              <span className="text-xs font-bold text-brand-accent uppercase tracking-widest">Udforsk vores arbejde</span>
              <h3 className="text-2xl font-bold text-brand-dark mt-1">Hvad vil du se mere om?</h3>
            </div>
            <Link
              to="/ydelser"
              className="inline-flex items-center gap-1.5 text-xs sm:text-sm font-bold text-brand-accent hover:text-brand-accent-hover"
            >
              <span>Se alle ydelser</span>
              <i className="fa-regular fa-arrow-right text-xs" />
            </Link>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Link
              to="/ydelser/portservice-og-lovpligtigt-eftersyn"
              className="group p-5 rounded-2xl border border-slate-100 bg-slate-50 hover:bg-white hover:border-brand-accent/30 hover:shadow-md transition-all flex flex-col justify-between"
            >
              <div className="flex items-center gap-3 mb-3">
                <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-brand-accent/10 text-brand-accent group-hover:bg-brand-accent group-hover:text-white transition-colors">
                  <i className="fa-solid fa-wrench text-sm" />
                </span>
                <h4 className="font-display text-sm font-bold text-brand-dark group-hover:text-brand-accent transition-colors">Portservice & Eftersyn</h4>
              </div>
              <p className="text-xs text-slate-500 leading-relaxed mb-3">
                Lovpligtigt årligt eftersyn, akut reparation og faste serviceaftaler for alle portmærker.
              </p>
              <span className="text-xs font-bold text-brand-accent inline-flex items-center gap-1 group-hover:gap-1.5 transition-all">
                Læs om portservice <i className="fa-regular fa-arrow-right text-[10px]" />
              </span>
            </Link>

            <Link
              to="/ydelser/industriporte"
              className="group p-5 rounded-2xl border border-slate-100 bg-slate-50 hover:bg-white hover:border-brand-accent/30 hover:shadow-md transition-all flex flex-col justify-between"
            >
              <div className="flex items-center gap-3 mb-3">
                <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-brand-accent/10 text-brand-accent group-hover:bg-brand-accent group-hover:text-white transition-colors">
                  <i className="fa-solid fa-industry text-sm" />
                </span>
                <h4 className="font-display text-sm font-bold text-brand-dark group-hover:text-brand-accent transition-colors">Industri- & Hurtigporte</h4>
              </div>
              <p className="text-xs text-slate-500 leading-relaxed mb-3">
                Slidstærke industriporte, ramper, sluser og hurtigporte tilpasset virksomhedens daglige drift.
              </p>
              <span className="text-xs font-bold text-brand-accent inline-flex items-center gap-1 group-hover:gap-1.5 transition-all">
                Læs om industriporte <i className="fa-regular fa-arrow-right text-[10px]" />
              </span>
            </Link>

            <Link
              to="/ydelser/alarmsystem"
              className="group p-5 rounded-2xl border border-slate-100 bg-slate-50 hover:bg-white hover:border-emerald-500/30 hover:shadow-md transition-all flex flex-col justify-between"
            >
              <div className="flex items-center gap-3 mb-3">
                <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-500/10 text-emerald-600 group-hover:bg-emerald-600 group-hover:text-white transition-colors">
                  <i className="fa-solid fa-shield-check text-sm" />
                </span>
                <h4 className="font-display text-sm font-bold text-brand-dark group-hover:text-emerald-600 transition-colors">Ajax Alarmsystemer</h4>
              </div>
              <p className="text-xs text-slate-500 leading-relaxed mb-3">
                Moderne trådløse alarmsystemer, HD-videoovervågning og smart adgangskontrol.
              </p>
              <span className="text-xs font-bold text-emerald-600 inline-flex items-center gap-1 group-hover:gap-1.5 transition-all">
                Læs om Ajax sikring <i className="fa-regular fa-arrow-right text-[10px]" />
              </span>
            </Link>

            <Link
              to="/galleri"
              className="group p-5 rounded-2xl border border-slate-100 bg-slate-50 hover:bg-white hover:border-brand-accent/30 hover:shadow-md transition-all flex flex-col justify-between"
            >
              <div className="flex items-center gap-3 mb-3">
                <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-brand-accent/10 text-brand-accent group-hover:bg-brand-accent group-hover:text-white transition-colors">
                  <i className="fa-solid fa-images text-sm" />
                </span>
                <h4 className="font-display text-sm font-bold text-brand-dark group-hover:text-brand-accent transition-colors">Billedgalleri</h4>
              </div>
              <p className="text-xs text-slate-500 leading-relaxed mb-3">
                Se fotos af udførte portmonteringer, reparationer og alarmopsætninger.
              </p>
              <span className="text-xs font-bold text-brand-accent inline-flex items-center gap-1 group-hover:gap-1.5 transition-all">
                Gå til galleri <i className="fa-regular fa-arrow-right text-[10px]" />
              </span>
            </Link>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="bg-brand-dark text-white px-6 py-20 md:py-24 text-center relative overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <img
            src="https://assets.auxodev.dk/assets/a2859b51-1b34-4d15-a97b-9fa5c0c0144f/webp-img-20220117-083136-01-scaled-68-11zon.webp"
            alt="Pabst Teknik portservice montør"
            className="h-full w-full object-cover"
          />
        </div>
        <div className="mx-auto max-w-4xl relative z-10 flex flex-col items-center gap-8">
          <h2 className="text-3xl font-extrabold sm:text-4xl text-white">Har du brug for professionel hjælp?</h2>
          <p className="text-lg text-slate-300 max-w-2xl leading-relaxed">
            Kontakt os i dag for en uforpligtende snak om, hvordan vi kan hjælpe dig med at sikre din bolig eller optimere din virksomheds porte og adgangskontrol.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto">
            <a
              href="tel:+4521684422"
              className="flex items-center justify-center gap-3 rounded-full bg-white px-8 py-4 text-base font-bold text-brand-dark shadow-lg transition-all hover:bg-slate-100"
            >
              <i className="fa-regular fa-phone text-brand-accent text-lg" />
              <span>Ring nu: 21 68 44 22</span>
            </a>
            <Link
              to="/kontakt"
              className="flex items-center justify-center gap-2 rounded-full bg-brand-accent px-8 py-4 text-base font-bold text-white shadow-lg transition-all hover:bg-brand-accent-hover"
            >
              <span>Skriv til os</span>
              <i className="fa-regular fa-envelope" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
